import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AdminService} from '../../../services/admin.service';
import {LabelSettings, ProjectSettings} from '../../../models/admin.model';
import {DialogService} from '../../../services/dialog.service';
import {BehaviorSubject, of} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '../../../services/user.service';
import {catchError, finalize, take} from "rxjs/operators";
import {environment} from "../../../../environments/environment";
import {Scope} from "../../../constants/scopes";
import {AuthService} from "../../../services/auth.service";

@Component({
  selector: 'app-settings-management',
  templateUrl: './settings-management.component.html',
  styleUrls: ['./settings-management.component.scss']
})
export class SettingsManagementComponent implements OnInit {

  public projectForm: FormGroup;

  public isLabelCreated = false;
  public isProjectSettingsCreated = false;

  public enableLabelBtn = false;
  public enableProjectSettingsBtn = false;

  public labelSettings: LabelSettings;
  public projectSettings: ProjectSettings;

  public loadingSubject = new BehaviorSubject<boolean>(false);
  public labelLoadingSubject = new BehaviorSubject<boolean>(false);
  public projectLoadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  public labelLoading$ = this.labelLoadingSubject.asObservable();
  public projectLoading$ = this.projectLoadingSubject.asObservable();

  clientId = environment.clientId;
  scopes = [Scope.EMAIL, Scope.OPENID, Scope.DRIVE_ADMIN_LABELS].join(' ');
  googleLoading = false;
  constructor(private adminService: AdminService,
              private authService: AuthService,
              private dialogService: DialogService,
              private userService: UserService,
              private fb: FormBuilder,
              private translate: TranslateService) {
  }

  ngOnInit(): void {
    this.projectForm = this.fb.group({
      gcpProject: ['', Validators.required],
      bucketName: ['', Validators.required]
    });
    this.getDomainConfiguration();
  }

  async getDomainConfiguration(): Promise<void> {
    this.loadingSubject.next(true);
    await this.fetchSettings();
    this.loadingSubject.next(false);
    if (this.isSuperAdmin()) {
      if (!this.isLabelCreated) {
        this.enableLabelBtn = true;
      }
      if (!this.isProjectSettingsCreated) {
        this.enableProjectSettingsBtn = true;
      }
    }
  }

  async fetchSettings() {
    this.labelSettings = await this.adminService.getLabelSettings().pipe(
      take(1),
      catchError(error => {
        console.error('Error fetching label settings:', error);
        this.dialogService.complianceError(error.error.message);

        return of(null);
      })
    ).toPromise();

    this.projectSettings = await this.adminService.getProjectSettings().pipe(
      take(1),
      catchError(error => {
        console.error('Error fetching project settings:', error);
        this.dialogService.complianceError(error.error.message);
        return of(null);
      })
    ).toPromise();

    if (!!this.labelSettings) {
      this.isLabelCreated = true;
    }
    if (!!this.projectSettings) {
      this.isProjectSettingsCreated = true;
    }
  }

  createDriveLabel(): void {
    this.labelLoadingSubject.next(true);
    this.adminService.createDriveLabel().pipe(
      finalize(() => this.labelLoadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.labelSettings = resp;
        this.enableLabelBtn = false;
        this.dialogService.info(this.translate.instant('finraSettings.dialog.savedMessage'))
          .afterClosed().subscribe(() => {
          this.isLabelCreated = true;
        });
      }, error: (error) => {
        this.googleLoading = false;
        this.enableLabelBtn = true;
        this.dialogService.complianceError(error.error.message);
      }
    });
  }

  enterProjectSettings(): void {
    this.projectLoadingSubject.next(true);
    const {gcpProject, bucketName} = this.projectForm.value;
    this.adminService.enterProjectSettings(gcpProject, bucketName).pipe(
      finalize(() => this.projectLoadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.projectSettings = resp;
        this.isProjectSettingsCreated = true;
        this.enableProjectSettingsBtn = false;
        this.dialogService.info(this.translate.instant('finraSettings.dialog.savedMessage'));
      }, error: (error) => {
        this.dialogService.complianceError(error.error.message);
      }
    });
  }

  isSuperAdmin(): boolean {
    return this.userService.isSuperAdmin();
  }

  getEnableProjectSettingsBtn(): boolean {
    return this.projectForm.valid && this.enableProjectSettingsBtn;
  }


  googleAuth(): void {
    setTimeout(() => {
      this.googleLoading = true;
    });
  }

  googleLoginSuccess(event: any): void {
    try {
      this.authService.signInWithGoogleWithAdditionalScopes(event.detail, this.scopes);
      this.createDriveLabel();
    } catch (e: any) {
      this.googleLoading = false;
      this.authService.authErrorMessage$.next(e.error);
    }
  }

  loginError(): void {
    this.googleLoading = false;
    this.authService.authErrorMessage$.next('Error: An error occurred during authentication');
  }
}
